import { useState, useEffect } from 'react';
import { hotelApi } from '../../hotelApi';

export default function HotelOnlineMenu({ ownersid }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState('');
  const [activeCat, setActiveCat] = useState(0);
  const [cart, setCart]         = useState([]);
  const [notes, setNotes]       = useState({});
  const [placing, setPlacing]   = useState(false);
  const [ordered, setOrdered]   = useState(false);
  const [showCart, setShowCart] = useState(false);

  // Read table number from URL ?table=T1
  const tableNo = (() => {
    try { return new URLSearchParams(window.location.search).get('table') || ''; } catch (_) { return ''; }
  })();

  useEffect(() => {
    if (!ownersid) { setErr('Invalid menu link'); setLoading(false); return; }
    hotelApi.getPublicMenu(ownersid, tableNo)
      .then(d => {
        if (d.error === 'table_not_found') setErr('Sorry, this table does not exist.');
        else setData(d);
      })
      .catch(() => setErr('Menu not available'))
      .finally(() => setLoading(false));
  }, [ownersid, tableNo]);

  const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  const addToCart = (item) => {
    setCart(c => {
      const ex = c.find(i => i.menuItemId === item.id);
      if (ex) return c.map(i => i.menuItemId === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...c, { menuItemId: item.id, name: item.name, price: item.price, quantity: 1 }];
    });
  };

  const removeFromCart = (menuItemId) => setCart(c => c.filter(i => i.menuItemId !== menuItemId));

  const setQty = (menuItemId, qty) => {
    if (qty <= 0) removeFromCart(menuItemId);
    else setCart(c => c.map(i => i.menuItemId === menuItemId ? { ...i, quantity: qty } : i));
  };

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const cartQty = (menuItemId) => {
    const item = cart.find(i => i.menuItemId === menuItemId);
    return item ? item.quantity : 0;
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) return;
    setPlacing(true);
    try {
      const items = cart.map(i => ({ menuItemId: i.menuItemId, quantity: i.quantity, notes: notes[i.menuItemId] || '' }));
      const res = await hotelApi.createPublicOrder(ownersid, { tableNo, items });
      if (res && res.success) {
        setOrdered(true);
        setCart([]);
        setShowCart(false);
      } else {
        alert('Order failed. Please try again.');
      }
    } catch (_) {
      alert('Could not place order. Please check your connection.');
    } finally {
      setPlacing(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg,#0d3321,#1a4731)' }}>
      <div style={{ color: '#fff', fontSize: 18 }}>Loading menu…</div>
    </div>
  );

  if (err || !data) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg,#0d3321,#1a4731)' }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: '40px 32px', textAlign: 'center',
        maxWidth: 340, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🪑</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#c5221f', marginBottom: 8 }}>{err || 'Menu not found'}</div>
        <div style={{ fontSize: 14, color: '#888', lineHeight: 1.6 }}>Please check the QR code or ask a staff member for assistance.</div>
      </div>
    </div>
  );

  if (ordered) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg,#0d3321,#1a4731)', fontFamily: 'Inter,system-ui,sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: '40px 32px', textAlign: 'center',
        maxWidth: 360, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <div style={{ fontWeight: 800, fontSize: 22, color: '#0d3321', marginBottom: 8 }}>Order Placed!</div>
        {tableNo && (
          <div style={{ background: '#e6f4ea', color: '#137333', fontWeight: 700, fontSize: 16,
            borderRadius: 10, padding: '8px 20px', display: 'inline-block', marginBottom: 12 }}>
            Table {tableNo}
          </div>
        )}
        <div style={{ color: '#5f6368', fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>
          Your order has been sent to the kitchen.<br />We'll have it ready for you shortly!
        </div>
        <button onClick={() => setOrdered(false)}
          style={{ marginTop: 24, background: '#137333', color: '#fff', border: 'none', borderRadius: 10,
            padding: '12px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          Order More
        </button>
      </div>
    </div>
  );

  const categories = data.categories || [];
  const activeCategory = categories[activeCat];

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', fontFamily: 'Inter,system-ui,sans-serif', paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg,#0d3321,#1a4731)', padding: '20px 20px 32px',
        textAlign: 'center', position: 'relative' }}>
        <div style={{ fontSize: 36, marginBottom: 6 }}>🏨</div>
        <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: 0 }}>{data.hotelName}</h1>
        {tableNo && (
          <div style={{ display: 'inline-block', background: 'rgba(255,255,255,.15)',
            color: '#fff', borderRadius: 20, padding: '5px 16px', fontSize: 14,
            fontWeight: 700, marginTop: 10, border: '1px solid rgba(255,255,255,.25)' }}>
            📍 Table {tableNo}
          </div>
        )}
      </div>

      {/* Category tabs */}
      {categories.length > 0 && (
        <div style={{ background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,.08)',
          position: 'sticky', top: 0, zIndex: 10, overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: 4, padding: '0 12px', minWidth: 'max-content' }}>
            {categories.map((cat, idx) => (
              <button key={idx} onClick={() => setActiveCat(idx)}
                style={{ background: 'none', border: 'none', padding: '13px 16px',
                  cursor: 'pointer', fontSize: 14, fontWeight: activeCat === idx ? 700 : 500,
                  color: activeCat === idx ? '#137333' : '#5f6368',
                  borderBottom: activeCat === idx ? '3px solid #137333' : '3px solid transparent',
                  whiteSpace: 'nowrap', transition: 'color .15s' }}>
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Menu items */}
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '16px 14px' }}>
        {categories.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🍽️</div>
            <div style={{ fontSize: 16 }}>No menu items available right now.</div>
          </div>
        )}

        {activeCategory && (
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0d3321', marginBottom: 12 }}>
              {activeCategory.name}
            </h2>
            <div style={{ display: 'grid', gap: 10 }}>
              {(activeCategory.items || []).map(item => {
                const qty = cartQty(item.id);
                return (
                  <div key={item.id} style={{ background: '#fff', borderRadius: 14, padding: '14px 16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,.06)', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between', gap: 12,
                    border: qty > 0 ? '2px solid #34a853' : '2px solid transparent', transition: 'border .15s' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                        <span style={{ fontSize: 14 }}>{item.is_veg ? '🟢' : '🔴'}</span>
                        <span style={{ fontWeight: 700, fontSize: 15, color: '#202124' }}>{item.name}</span>
                      </div>
                      {item.description && (
                        <div style={{ fontSize: 12, color: '#5f6368', lineHeight: 1.5 }}>{item.description}</div>
                      )}
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#137333', marginTop: 4 }}>
                        {fmtRs(item.price)}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      {qty === 0 ? (
                        <button onClick={() => addToCart(item)}
                          style={{ background: '#137333', color: '#fff', border: 'none', borderRadius: 20,
                            padding: '7px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                          + Add
                        </button>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                          background: '#e6f4ea', borderRadius: 20, padding: '4px 10px' }}>
                          <button onClick={() => setQty(item.id, qty - 1)}
                            style={{ background: '#137333', color: '#fff', border: 'none', borderRadius: '50%',
                              width: 26, height: 26, cursor: 'pointer', fontWeight: 800, fontSize: 16, lineHeight: 1 }}>
                            −
                          </button>
                          <span style={{ fontWeight: 800, fontSize: 16, color: '#0d3321', minWidth: 18, textAlign: 'center' }}>
                            {qty}
                          </span>
                          <button onClick={() => addToCart(item)}
                            style={{ background: '#137333', color: '#fff', border: 'none', borderRadius: '50%',
                              width: 26, height: 26, cursor: 'pointer', fontWeight: 800, fontSize: 16, lineHeight: 1 }}>
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Floating cart button */}
      {cartCount > 0 && !showCart && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 100 }}>
          <button onClick={() => setShowCart(true)}
            style={{ background: '#137333', color: '#fff', border: 'none', borderRadius: 30,
              padding: '14px 32px', cursor: 'pointer', fontWeight: 700, fontSize: 16,
              boxShadow: '0 6px 24px rgba(19,115,51,.5)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: '#fff', color: '#137333', borderRadius: '50%', width: 24, height: 24,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>
              {cartCount}
            </span>
            View Order · {fmtRs(cartTotal)}
          </button>
        </div>
      )}

      {/* Cart sheet */}
      {showCart && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', flexDirection: 'column',
          justifyContent: 'flex-end' }}>
          <div style={{ flex: 1, background: 'rgba(0,0,0,.4)' }} onClick={() => setShowCart(false)} />
          <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', maxHeight: '80vh',
            overflow: 'auto', padding: '20px 16px 32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: '#0d3321' }}>
                Your Order {tableNo && <span style={{ color: '#137333', fontSize: 14, fontWeight: 600 }}>· Table {tableNo}</span>}
              </div>
              <button onClick={() => setShowCart(false)}
                style={{ background: '#f1f3f4', border: 'none', borderRadius: '50%', width: 32, height: 32,
                  cursor: 'pointer', fontSize: 18, fontWeight: 700 }}>×</button>
            </div>

            {cart.map(item => (
              <div key={item.menuItemId} style={{ display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 0', borderBottom: '1px solid #f1f3f4' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{item.name}</div>
                  <div style={{ fontSize: 13, color: '#137333', fontWeight: 700 }}>{fmtRs(item.price)}</div>
                  <input placeholder="Special instructions…" value={notes[item.menuItemId] || ''}
                    onChange={e => setNotes(n => ({ ...n, [item.menuItemId]: e.target.value }))}
                    style={{ marginTop: 4, fontSize: 12, border: '1px solid #e8eaed', borderRadius: 6,
                      padding: '4px 8px', width: '100%', outline: 'none' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <button onClick={() => setQty(item.menuItemId, item.quantity - 1)}
                    style={{ background: item.quantity === 1 ? '#fce8e6' : '#f1f3f4', color: item.quantity === 1 ? '#c5221f' : '#202124',
                      border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontWeight: 800 }}>
                    {item.quantity === 1 ? '🗑' : '−'}
                  </button>
                  <span style={{ fontWeight: 800, fontSize: 15, minWidth: 20, textAlign: 'center' }}>{item.quantity}</span>
                  <button onClick={() => setQty(item.menuItemId, item.quantity + 1)}
                    style={{ background: '#e6f4ea', color: '#137333', border: 'none', borderRadius: '50%',
                      width: 28, height: 28, cursor: 'pointer', fontWeight: 800 }}>+</button>
                </div>
                <div style={{ minWidth: 60, textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
                  {fmtRs(item.price * item.quantity)}
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800,
              fontSize: 17, padding: '14px 0 4px', borderTop: '2px solid #e8eaed', marginTop: 8 }}>
              <span>Total</span><span style={{ color: '#137333' }}>{fmtRs(cartTotal)}</span>
            </div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>Taxes & charges as applicable</div>

            <button onClick={handlePlaceOrder} disabled={placing}
              style={{ width: '100%', background: '#137333', color: '#fff', border: 'none', borderRadius: 14,
                padding: '15px', fontSize: 17, fontWeight: 800, cursor: placing ? 'not-allowed' : 'pointer',
                opacity: placing ? 0.7 : 1 }}>
              {placing ? 'Placing Order…' : `🍽️ Place Order${tableNo ? ' for Table ' + tableNo : ''}`}
            </button>
          </div>
        </div>
      )}

      <div style={{ textAlign: 'center', padding: '20px', color: '#9aa0a6', fontSize: 12 }}>
        Powered by SmaBizZ · Hotel Management System
      </div>
    </div>
  );
}
